if (!customElements.get('scroll-tab-switch')) {
  class ScrollTabSwitch extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onTabClick = this._onTabClick.bind(this);
      this._isProgrammaticScroll = false;
      this._programmaticScrollTimer = null;
      this._stickyOffset = 0;
      this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      this.nav = this.querySelector('.sts-nav');
      this.tabs = Array.from(this.querySelectorAll('.sts-tab'));
      this.panels = Array.from(this.querySelectorAll('.sts-panel'));

      if (!this.tabs.length || !this.panels.length) return;

      // Find the theme's header element so we can track its real,
      // on-screen position directly rather than relying on
      // --sticky-header-height / --header-height custom properties,
      // which can lag or reset during an absolute -> fixed (or
      // hidden -> shown) transition and leave a gap above the nav.
      this._headerEl = document.querySelector('header.header') || document.querySelector('header');

      this.tabs.forEach((tab) => tab.addEventListener('click', this._onTabClick));

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });

      this._updateNavOffset();

      requestAnimationFrame(() => {
        this._updateActive();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._programmaticScrollTimer) clearTimeout(this._programmaticScrollTimer);
      if (this._scrollAnimationId) cancelAnimationFrame(this._scrollAnimationId);
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    _prefersReducedMotion() {
      return this._reducedMotionQuery.matches;
    }

    /**
     * Reads the header's current bottom edge directly from the DOM
     * (getBoundingClientRect, relative to the viewport) and pins
     * .sts-nav's `top` to that value every scroll/resize tick.
     *
     * Why this approach: this theme's header switches between
     * `position: absolute` (transparent, overlaying the hero on
     * index/collection templates) and `position: fixed` (once
     * `.header-scrolled` is added), and/or hides/shows depending on
     * sticky_header_type. CSS custom properties meant to track the
     * header's height can lag or reset during those transitions,
     * which is what produced the gap above .sts-nav on scroll-up.
     * Measuring the header's real rect every frame sidesteps all of
     * that — whatever the header is currently doing, the nav simply
     * sits right below its visible bottom edge (or top: 0 if the
     * header has scrolled out of view, in which case bottom <= 0).
     */
    _updateNavOffset() {
      if (!this.nav) return;

      let headerBottom = 0;
      if (this._headerEl) {
        headerBottom = Math.max(0, Math.round(this._headerEl.getBoundingClientRect().bottom));
      }

      this.nav.style.top = headerBottom + 'px';
      this._stickyOffset = headerBottom + this.nav.offsetHeight;
    }

    _getStickyOffset() {
      return this._stickyOffset;
    }

    _onTabClick(event) {
      const index = parseInt(event.currentTarget.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this._scrollToPanel(index);
    }

    _scrollToPanel(index) {
      const panel = this.panels[index];
      if (!panel) return;

      const offset = this._getStickyOffset();
      const top = panel.getBoundingClientRect().top + window.scrollY - offset - 8;

      this._setActive(index);

      if (this._prefersReducedMotion()) {
        // Respect the user's OS-level preference: jump straight there
        // instead of running the ~1s custom animation.
        window.scrollTo(0, Math.max(0, top));
        return;
      }

      const duration = 900;

      this._isProgrammaticScroll = true;
      if (this._programmaticScrollTimer) clearTimeout(this._programmaticScrollTimer);
      this._programmaticScrollTimer = setTimeout(() => {
        this._isProgrammaticScroll = false;
      }, duration + 100);

      this._animateScrollTo(top, duration);
    }

    _animateScrollTo(targetY, duration) {
      if (this._scrollAnimationId) cancelAnimationFrame(this._scrollAnimationId);
      const startY = window.scrollY;
      const diff = targetY - startY;
      if (diff === 0) return;
      const startTime = performance.now();
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        window.scrollTo(0, startY + diff * ease(progress));
        if (progress < 1) {
          this._scrollAnimationId = requestAnimationFrame(step);
        } else {
          this._scrollAnimationId = null;
        }
      };

      this._scrollAnimationId = requestAnimationFrame(step);
    }

    _onScroll() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._updateNavOffset();
        if (!this._isProgrammaticScroll) this._updateActive();
      });
    }

    _onResize() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._updateNavOffset();
        this._updateActive();
      });
    }

    _updateActive() {
      const offset = this._getStickyOffset() + 20;
      let activeIndex = 0;

      for (let i = 0; i < this.panels.length; i++) {
        const top = this.panels[i].getBoundingClientRect().top;
        if (top - offset <= 0) {
          activeIndex = i;
        } else {
          break;
        }
      }

      this._setActive(activeIndex);
    }

    _setActive(index) {
      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('sts-tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this._scrollActiveTabIntoView(index);
    }

    _scrollActiveTabIntoView(index) {
      const tab = this.tabs[index];
      const scroller = this.querySelector('.sts-nav__scroll');
      if (!tab || !scroller) return;

      const tabRect = tab.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();

      if (tabRect.left < scrollerRect.left || tabRect.right > scrollerRect.right) {
        const target = tab.offsetLeft - (scroller.clientWidth / 2) + (tab.offsetWidth / 2);
        scroller.scrollTo({
          left: Math.max(0, target),
          behavior: this._prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
    }
  }

  customElements.define('scroll-tab-switch', ScrollTabSwitch);
}